import React, { useState, useEffect } from 'react';
import { Globe } from 'lucide-react';

const FloatingTranslate = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);

  useEffect(() => {
    // Load Google Translate script
    const loadGoogleTranslateScript = () => {
      // Check if script already exists
      if (document.querySelector('script[src*="translate.google.com"]')) {
        setScriptLoaded(true);
        return;
      }

      // Create and load the script
      const script = document.createElement('script');
      script.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
      script.async = true;
      
      // Define global callback
      window.googleTranslateElementInit = () => {
        console.log('Google Translate script loaded');
        setScriptLoaded(true);
      };
      
      script.onload = () => {
        console.log('Google Translate script loaded successfully');
      };
      
      script.onerror = () => {
        console.error('Failed to load Google Translate script');
      };
      
      document.head.appendChild(script);
    };

    // Load script on component mount
    loadGoogleTranslateScript();
  }, []);

  // Enhanced translation function
  const doTranslate = (langCode) => {
    setIsOpen(false);
    
    try {
      // Clear any existing Google Translate cookies
      const cookiesToClear = ['googtrans', 'googtrans-TL'];
      cookiesToClear.forEach(cookieName => {
        document.cookie = `${cookieName}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=.${window.location.hostname}`;
        document.cookie = `${cookieName}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
      });
      
      // Set new translation cookie
      const translationValue = `/auto/${langCode}`;
      document.cookie = `googtrans=${translationValue}; path=/; max-age=86400`;
      document.cookie = `googtrans=${translationValue}; path=/; max-age=86400; domain=.${window.location.hostname}`;
      
      // Store preference in localStorage
      localStorage.setItem('preferredLanguage', langCode);
      
      // Force page reload to apply translation
      setTimeout(() => {
        window.location.reload();
      }, 200);
      
    } catch (error) {
      console.error('Translation error:', error);
      // Fallback: just reload the page
      window.location.reload();
    }
  };

  // Enhanced reset function
  const resetTranslation = () => {
    setIsOpen(false);
    
    try {
      // Clear all translation cookies
      const cookiesToClear = ['googtrans', 'googtrans-TL'];
      cookiesToClear.forEach(cookieName => {
        document.cookie = `${cookieName}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=.${window.location.hostname}`;
        document.cookie = `${cookieName}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
      });
      
      // Clear localStorage preference
      localStorage.removeItem('preferredLanguage');
      
      // Force page reload
      setTimeout(() => {
        window.location.reload();
      }, 200);
      
    } catch (error) {
      console.error('Reset translation error:', error);
      window.location.reload();
    }
  };

  // Enhanced language detection
  const getCurrentLanguage = () => {
    // Check localStorage first
    const stored = localStorage.getItem('preferredLanguage');
    if (stored && ['hi', 'en', 'mr'].includes(stored)) {
      return stored;
    }
    
    // Check Google Translate cookies
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'googtrans' && value) {
        if (value.includes('/en')) return 'en';
        if (value.includes('/mr')) return 'mr';
        if (value.includes('/hi')) return 'hi';
      }
    }
    
    // Check if page is currently translated by looking at Google Translate elements
    if (document.querySelector('.goog-te-banner-frame') || 
        document.querySelector('.skiptranslate') ||
        document.body.classList.contains('translated-ltr') ||
        document.body.classList.contains('translated-rtl')) {
      // Page is translated, try to detect language from URL or other indicators
      const currentUrl = window.location.href;
      if (currentUrl.includes('_x_tr_tl=en')) return 'en';
      if (currentUrl.includes('_x_tr_tl=mr')) return 'mr';
    }
    
    return 'hi'; // Default to Hindi
  };

  const currentLang = getCurrentLanguage();

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {/* Floating Translate Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="bg-orange-600 hover:bg-orange-700 text-white p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110 group relative"
        aria-label="भाषा बदलें"
        title="भाषा बदलें / Change Language"
      >
        <Globe className="w-6 h-6 group-hover:rotate-12 transition-transform duration-300" />
        
        {/* Enhanced Language indicator */}
        <div className={`absolute -top-2 -right-2 w-7 h-7 rounded-full border-2 border-white text-xs font-bold flex items-center justify-center ${
          currentLang === 'hi' ? 'bg-orange-500 text-white' : 
          currentLang === 'en' ? 'bg-blue-500 text-white' : 'bg-green-500 text-white'
        }`}>
          {currentLang === 'hindi' ? 'भाषा' : currentLang === 'en' ? 'EN' : 'भाषा'}
        </div>
      </button>

      {/* Enhanced Translate Widget Popup */}
      {isOpen && (
        <div className="absolute bottom-16 right-0 bg-white rounded-lg shadow-xl border border-gray-200 p-4 min-w-[300px] max-w-[340px] transform transition-all duration-300 animate-in slide-in-from-bottom-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
              <Globe className="w-4 h-4 text-orange-600" />
              भाषा चुनें / Choose Language
            </h3>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1"
              aria-label="Close"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Language Selection Buttons */}
          <div className="space-y-2">
            <button 
              onClick={() => doTranslate('hi')}
              className={`w-full px-4 py-3 text-left rounded-lg transition-all duration-200 flex items-center gap-3 group ${
                currentLang === 'hi' 
                  ? 'bg-orange-100 text-orange-800 border-2 border-orange-300 shadow-sm' 
                  : 'bg-orange-50 hover:bg-orange-100 text-orange-800 border border-orange-200 hover:shadow-sm'
              }`}
            >
              <div className="w-4 h-4 bg-orange-500 rounded-full flex items-center justify-center">
                {currentLang === 'hi' && <span className="text-white text-xs">✓</span>}
              </div>
              <div className="flex-1">
                <div className="font-medium">हिंदी</div>
                <div className="text-xs text-orange-600">मूल भाषा / Original</div>
              </div>
            </button>
            
            <button 
              onClick={() => doTranslate('en')}
              className={`w-full px-4 py-3 text-left rounded-lg transition-all duration-200 flex items-center gap-3 group ${
                currentLang === 'en' 
                  ? 'bg-blue-100 text-blue-800 border-2 border-blue-300 shadow-sm' 
                  : 'bg-blue-50 hover:bg-blue-100 text-blue-800 border border-blue-200 hover:shadow-sm'
              }`}
            >
              <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                {currentLang === 'en' && <span className="text-white text-xs">✓</span>}
              </div>
              <div className="flex-1">
                <div className="font-medium">English</div>
                <div className="text-xs text-blue-600">Translate to English</div>
              </div>
            </button>
            
            <button 
              onClick={() => doTranslate('mr')}
              className={`w-full px-4 py-3 text-left rounded-lg transition-all duration-200 flex items-center gap-3 group ${
                currentLang === 'mr' 
                  ? 'bg-green-100 text-green-800 border-2 border-green-300 shadow-sm' 
                  : 'bg-green-50 hover:bg-green-100 text-green-800 border border-green-200 hover:shadow-sm'
              }`}
            >
              <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                {currentLang === 'mr' && <span className="text-white text-xs">✓</span>}
              </div>
              <div className="flex-1">
                <div className="font-medium">मराठी</div>
                <div className="text-xs text-green-600">मराठीत भाषांतर</div>
              </div>
            </button>
          </div>
          
          {/* Reset Button */}
          <button 
            onClick={resetTranslation}
            className="w-full mt-4 px-4 py-2 text-center bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors text-sm font-medium border border-gray-200"
          >
            🔄 मूल भाषा में वापस / Reset to Original
          </button>
          
          {/* Status and Instructions */}
          <div className="mt-4 pt-3 border-t border-gray-100">
            <div className="text-xs text-gray-500 text-center space-y-1">
              <div className="font-medium">
                वर्तमान भाषा: {currentLang === 'hi' ? 'हिंदी' : currentLang === 'en' ? 'English' : 'मराठी'}
              </div>
              <div className="text-gray-400">
                {scriptLoaded ? '🟢 Google Translate Ready' : '🟡 Loading...'}
              </div>
              <div className="text-gray-400">
                पूरी वेबसाइट अनुवादित होगी
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FloatingTranslate;